export type Sex = "male" | "female";
export type ActivityLevel = "sedentary" | "light" | "moderate" | "very_active";
export type Objective = "cut" | "maintain" | "build";

export type Profile = {
  sex: Sex;
  age: number;
  heightCm: number;
  weightKg: number;
  activity: ActivityLevel;
  objective: Objective;
};

export const ACTIVITY_MULTIPLIER: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  very_active: 1.725,
};

export const OBJECTIVE_CAL_DELTA: Record<Objective, number> = {
  cut: -500,
  maintain: 0,
  build: 250,
};

export const OBJECTIVE_PROTEIN_PER_KG: Record<Objective, number> = {
  cut: 2.0,
  maintain: 1.6,
  build: 1.8,
};

export function computeGoals(p: Profile): {
  bmr: number;
  tdee: number;
  caloriesGoal: number;
  proteinGoalG: number;
} {
  const bmr = 10 * p.weightKg + 6.25 * p.heightCm - 5 * p.age + (p.sex === "male" ? 5 : -161);
  const tdee = bmr * ACTIVITY_MULTIPLIER[p.activity];
  const caloriesGoal = Math.round((tdee + OBJECTIVE_CAL_DELTA[p.objective]) / 10) * 10;
  const proteinGoalG = Math.round(p.weightKg * OBJECTIVE_PROTEIN_PER_KG[p.objective]);
  return { bmr, tdee, caloriesGoal, proteinGoalG };
}

export type ProteinLevel = 0 | 1 | 2 | 3;

export function proteinLevel(proteinG: number, floor: number): ProteinLevel {
  if (proteinG <= 0) return 0;
  const r = proteinG / floor;
  if (r < 0.5) return 1;
  if (r < 1.0) return 2;
  return 3;
}

export type CalorieStatus = "none" | "ok" | "warn" | "over";

export function calorieStatus(cal: number, ceiling: number): CalorieStatus {
  if (cal <= 0) return "none";
  const r = cal / ceiling;
  if (r <= 1.0) return "ok";
  if (r <= 1.15) return "warn";
  return "over";
}
